import React, { useEffect, useState, useMemo } from "react";
import HeaderPrint from "../../../Pdf/PrintHeaderAccount";
import axios from "axios";
import config from "../../../config";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import { Spin } from "antd";
import moment from "moment";

const COL = {
  leftLabel: 170,
  leftAmount: 113,
  rightLabel: 170,
  rightAmount: 114,
};

const TABLE_WIDTH =
  COL.leftLabel + COL.leftAmount + COL.rightLabel + COL.rightAmount;

const ASSET_ROWS = [
  { label: "Inventory (at cost)", key: "stockValue" },
  { label: "Customer receivable", key: "customerReceivable" },
  { label: "Supplier receivable", key: "supplierReceivable" },
  { label: "Other receivables", key: "otherReceivable" },
  { label: "Cash in hand", key: "cashInHand" },
  { label: "Cash at bank", key: "cashAtBank" },
];

const LIABILITY_ROWS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Other payables", key: "otherPayable" },
];

const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

function buildSideBySideRows(data) {
  const leftRows = ASSET_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const rightRows = LIABILITY_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const count = Math.max(leftRows.length, rightRows.length);
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({ left: leftRows[i], right: rightRows[i] });
  }
  return rows;
}

const PrintCapitalReport = () => {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const urlParams = new URLSearchParams(window.location.search);
  const AsOfDate = urlParams.get("AsOfDate") || moment().format("YYYY-MM-DD");

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const formattedAsOf = moment(AsOfDate).isValid()
    ? moment(AsOfDate).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  useEffect(() => {
    document.title = "Print Capital Report";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetCapitalReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        Dated: formattedAsOf,
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setData(response.data);
        } else {
          setData({});
        }
        setLoading(false);
      })
      .catch(() => {
        setData({});
        setLoading(false);
      });
  }, [formattedAsOf, UserID, AccessKey]);

  const asOfLabel =
    data.asOfDate && moment(data.asOfDate).isValid()
      ? moment(data.asOfDate).format(config.date_format)
      : moment(formattedAsOf).format(config.date_format);

  const tableRows = useMemo(() => buildSideBySideRows(data), [data]);

  const totals = useMemo(() => {
    const totalAssets =
      parseReportAmount(data.totalAssets) ||
      ASSET_ROWS.reduce((sum, row) => sum + parseReportAmount(data[row.key]), 0);
    const totalTradeLiabilities =
      parseReportAmount(data.totalTradeLiabilities) ||
      LIABILITY_ROWS.reduce(
        (sum, row) => sum + parseReportAmount(data[row.key]),
        0
      );
    const netOwnersCapital =
      parseReportAmount(data.netOwnersCapital) ||
      totalAssets - totalTradeLiabilities;
    const capitalLedgerBalance = parseReportAmount(data.capitalLedgerBalance);
    const ledgerVariance =
      parseReportAmount(data.ledgerVariance) ||
      netOwnersCapital - capitalLedgerBalance;
    return {
      totalAssets,
      totalTradeLiabilities,
      netOwnersCapital,
      capitalLedgerBalance,
      ledgerVariance,
    };
  }, [data]);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <HeaderPrint compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Capital Report</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              As of: {asOfLabel}
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Owner&apos;s equity: assets less trade payables
            </Text>
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={[saleReportStyles.tableHead, { width: TABLE_WIDTH }]}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Total Assets</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>Trade Payables</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {tableRows.map((row, index) => (
              <View key={index} style={saleReportStyles.row}>
                <View style={[saleReportStyles.td, { width: COL.leftLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.left?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.leftAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.left ? fmt(row.left.amount) : ""}
                  </Text>
                </View>
                <View style={[saleReportStyles.td, { width: COL.rightLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.right?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.rightAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.right ? fmt(row.right.amount) : ""}
                  </Text>
                </View>
              </View>
            ))}

            <View style={saleReportStyles.totalsRow}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Total assets</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalAssets)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>
                  Total trade payables
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalTradeLiabilities)}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={[
              saleReportStyles.table,
              { width: TABLE_WIDTH, marginTop: 8 },
            ]}
          >
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  Net owner&apos;s capital
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.netOwnersCapital)}
                </Text>
              </View>
            </View>
            <View style={saleReportStyles.row}>
              <View
                style={[
                  saleReportStyles.td,
                  {
                    width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.tdValue}>
                  Capital per ledger
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.td,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.tdValue}>
                  {fmt(totals.capitalLedgerBalance)}
                </Text>
              </View>
            </View>
            {Math.abs(totals.ledgerVariance) > 0.5 && (
              <View style={saleReportStyles.row}>
                <View
                  style={[
                    saleReportStyles.td,
                    {
                      width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                      textAlign: "right",
                    },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    Reconciliation variance
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.rightAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {fmt(totals.ledgerVariance)}
                  </Text>
                </View>
              </View>
            )}
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="CapitalReport.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintCapitalReport;
